var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};

// libs/sdk/src/lib/tokens/token-rate/base-adapters/uniswap-v2-base-rate-adapter.ts
import { getAddress, isAddressEqual, parseAbi, zeroAddress } from "viem";
import { getClient } from "@one-inch-community/sdk/chain";
import { BigMath } from "@one-inch-community/core/math";
import { LongTimeCache } from "@one-inch-community/core/cache";
var FactoryContractABI = parseAbi([
  "function getPair(address tokenA, address tokenB) external view returns (address pair)"
]);
var PoolContractABI = parseAbi([
  "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
  "function token0() external view returns (address)",
  "function token1() external view returns (address)"
]);
var UniswapV2BaseRateAdapter = class {
  constructor(name, factoryContractGetter3, supportedChain) {
    this.name = name;
    this.factoryContractGetter = factoryContractGetter3;
    this.supportedChain = supportedChain;
    this.pools = new LongTimeCache(`${this.name}_pools`, 7);
  }
  isSupportedChain(chainId) {
    return this.supportedChain.includes(chainId);
  }
  async getRate(chainId, sourceToken, destinationToken) {
    try {
      const srcTokenAddress = getAddress(sourceToken.address);
      const dstTokenAddress = getAddress(destinationToken.address);
      const [pool, token0] = await this.getPool(chainId, srcTokenAddress, dstTokenAddress);
      if (pool === zeroAddress) {
        return null;
      }
      const reserves = await this.getReserves(chainId, pool);
      const lastBlockTimestamp = reserves[2];
      if (Date.now() - lastBlockTimestamp * 1e3 > 36e5) {
        return null;
      }
      const isRevertRate = !isAddressEqual(sourceToken.address, token0);
      const rate = BigMath.div(
        isRevertRate ? reserves[0] : reserves[1],
        isRevertRate ? reserves[1] : reserves[0],
        destinationToken.decimals,
        sourceToken.decimals,
        destinationToken.decimals
      );
      const revertedRate = BigMath.div(
        isRevertRate ? reserves[1] : reserves[0],
        isRevertRate ? reserves[0] : reserves[1],
        sourceToken.decimals,
        destinationToken.decimals,
        sourceToken.decimals
      );
      return {
        chainId,
        rate,
        revertedRate,
        isReverted: isRevertRate,
        sourceToken,
        destinationToken
      };
    } catch (error) {
      console.error(`Error in UniswapV2BaseAdapter adapter name ${this.name}`, error);
      return null;
    }
  }
  async getPool(chainId, srcTokenAddress, dstTokenAddress) {
    const id1 = [chainId, srcTokenAddress, dstTokenAddress].join(":");
    if (this.pools.has(id1)) {
      return this.pools.get(id1);
    }
    const id2 = [chainId, dstTokenAddress, srcTokenAddress].join(":");
    if (this.pools.has(id2)) {
      return this.pools.get(id2);
    }
    const client = getClient(chainId);
    let pool = await client.readContract({
      address: getAddress(this.factoryContractGetter(chainId)),
      functionName: "getPair",
      args: [srcTokenAddress, dstTokenAddress],
      abi: FactoryContractABI
    });
    if (pool !== zeroAddress) {
      pool = await client.readContract({
        address: getAddress(this.factoryContractGetter(chainId)),
        functionName: "getPair",
        args: [dstTokenAddress, srcTokenAddress],
        abi: FactoryContractABI
      });
    }
    let token0 = zeroAddress;
    if (pool !== zeroAddress) {
      token0 = await client.readContract({
        address: pool,
        abi: PoolContractABI,
        functionName: "token0"
      });
    }
    this.pools.set(id1, [pool, token0]);
    this.pools.set(id2, [pool, token0]);
    return [pool, token0];
  }
  async getReserves(chainId, pool) {
    const client = getClient(chainId);
    return await client.readContract({
      address: pool,
      abi: PoolContractABI,
      functionName: "getReserves"
    });
  }
};

// libs/sdk/src/lib/tokens/token-rate/adapters/uniswap-v2-adapter.ts
import { ChainId } from "@one-inch-community/models";
var uniswapV2Adapter = new UniswapV2BaseRateAdapter(
  "UniswapV2",
  () => "0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f",
  [
    ChainId.eth,
    ChainId.bnb,
    ChainId.matic,
    ChainId.op,
    ChainId.arbitrum,
    ChainId.avalanche
  ]
);

// libs/sdk/src/lib/tokens/token-rate/adapters/sushiswap-v2-adapter.ts
import { ChainId as ChainId2 } from "@one-inch-community/models";
var SushiSwapFactory = {
  [ChainId2.eth]: "0xC0AeE478e3658e2610c5F7A4A2E1777cE9e4f2Ac",
  [ChainId2.bnb]: "0xc35dadb65012ec5796536bd9864ed8773abc74c4",
  [ChainId2.matic]: "0xc35dadb65012ec5796536bd9864ed8773abc74c4",
  [ChainId2.gnosis]: "0xc35dadb65012ec5796536bd9864ed8773abc74c4",
  [ChainId2.fantom]: "0xc35dadb65012ec5796536bd9864ed8773abc74c4",
  [ChainId2.avalanche]: "0xc35dadb65012ec5796536bd9864ed8773abc74c4",
  [ChainId2.arbitrum]: "0xc35dadb65012ec5796536bd9864ed8773abc74c4"
};
var sushiswapV2Adapter = new UniswapV2BaseRateAdapter(
  "SushiSwapV2",
  (chainId) => SushiSwapFactory[chainId],
  [
    ChainId2.eth,
    ChainId2.bnb,
    ChainId2.matic,
    ChainId2.gnosis,
    ChainId2.fantom,
    ChainId2.arbitrum,
    ChainId2.avalanche
  ]
);

// libs/sdk/src/lib/tokens/token-rate/adapters/pancakeswap-v2-adapter.ts
import { ChainId as ChainId3 } from "@one-inch-community/models";
var PancakeSwapFactory = {
  [ChainId3.eth]: "0x1097053Fd2ea711dad45caCcc45EfF7548fCB362",
  [ChainId3.bnb]: "0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73",
  [ChainId3.arbitrum]: "0x02a84c1b3BBD7401a5f7fa98a384EBC70bB5749E"
};
var pancakeswapV2Adapter = new UniswapV2BaseRateAdapter(
  "PancakeSwapV2",
  (chainId) => PancakeSwapFactory[chainId],
  [
    ChainId3.eth,
    ChainId3.bnb,
    ChainId3.arbitrum
  ]
);

// libs/sdk/src/lib/tokens/token-rate/token-rate.provider.ts
import {
  getBlockEmitter,
  getWrapperNativeToken,
  isNativeToken
} from "@one-inch-community/sdk/chain";
import { startWith, switchMap } from "rxjs";

// libs/sdk/src/lib/tokens/token-rate/adapters/uniswap-v3-adapter.ts
import { ChainId as ChainId4 } from "@one-inch-community/models";

// libs/sdk/src/lib/tokens/token-rate/base-adapters/uniswap-v3-base-rate-adapter.ts
import { getAddress as getAddress2, isAddressEqual as isAddressEqual2, parseAbi as parseAbi2, zeroAddress as zeroAddress2 } from "viem";
import { getClient as getClient2 } from "@one-inch-community/sdk/chain";
import { LongTimeCache as LongTimeCache2 } from "@one-inch-community/core/cache";
import { BlockTimeCache } from "@one-inch-community/sdk/chain";
import { BigMath as BigMath2 } from "@one-inch-community/core/math";
var FactoryContractABI2 = parseAbi2([
  "function getPool(address tokenA, address tokenB, uint24 fee) external view returns (address pool)"
]);
var PoolContractABI2 = parseAbi2([
  "function liquidity() external view returns (uint128)",
  "function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)",
  "function token0() external view returns (address)",
  "function token1() external view returns (address)"
]);
var UniswapV3BaseRateAdapter = class {
  constructor(name, factoryContractGetter3, supportedChain, feeTiers) {
    this.name = name;
    this.factoryContractGetter = factoryContractGetter3;
    this.supportedChain = supportedChain;
    this.feeTiers = feeTiers;
    this.pools = new LongTimeCache2(`${this.name}_pools`, 7);
    this.liquidityCache = new BlockTimeCache();
  }
  isSupportedChain(chainId) {
    return this.supportedChain.includes(chainId);
  }
  async getRate(chainId, sourceToken, destinationToken) {
    try {
      const srcTokenAddress = getAddress2(sourceToken.address);
      const dstTokenAddress = getAddress2(destinationToken.address);
      const [pool, token0] = await this.getPool(chainId, srcTokenAddress, dstTokenAddress);
      if (pool === zeroAddress2) {
        return null;
      }
      const isRevertRate = !isAddressEqual2(sourceToken.address, token0);
      const [sqrtPriceX96, liquidity] = await Promise.all([
        this.getSqrtPriceX96(chainId, pool),
        this.getLiquidity(chainId, pool)
      ]);
      const [rate, revertedRate] = this.calculatePrice(sqrtPriceX96, sourceToken, destinationToken, liquidity, isRevertRate);
      return {
        chainId,
        rate,
        revertedRate,
        sourceToken,
        destinationToken,
        isReverted: isRevertRate
      };
    } catch (error) {
      console.error(`Error in UniswapV3BaseRateAdapter adapter name ${this.name}`, error);
      return null;
    }
  }
  async getPool(chainId, srcTokenAddress, dstTokenAddress) {
    const id1 = [chainId, srcTokenAddress, dstTokenAddress].join(":");
    if (this.pools.has(id1)) {
      return this.pools.get(id1);
    }
    const id2 = [chainId, dstTokenAddress, srcTokenAddress].join(":");
    if (this.pools.has(id2)) {
      return this.pools.get(id2);
    }
    const client = getClient2(chainId);
    let resultPool = zeroAddress2;
    let maxLiquidity = 0n;
    for (const fee of this.feeTiers) {
      const pool = await client.readContract({
        address: getAddress2(this.factoryContractGetter(chainId)),
        functionName: "getPool",
        args: [srcTokenAddress, dstTokenAddress, fee],
        abi: FactoryContractABI2
      });
      if (pool === zeroAddress2) {
        continue;
      }
      const liquidity = await this.getLiquidity(chainId, pool);
      if (liquidity > maxLiquidity) {
        maxLiquidity = liquidity;
        resultPool = pool;
      }
    }
    if (resultPool !== zeroAddress2) {
      const token0 = await client.readContract({
        address: resultPool,
        abi: PoolContractABI2,
        functionName: "token0"
      });
      this.pools.set(id1, [resultPool, token0]);
      this.pools.set(id2, [resultPool, token0]);
      return [resultPool, token0];
    }
    return [zeroAddress2, zeroAddress2];
  }
  async getSqrtPriceX96(chainId, pool) {
    const client = getClient2(chainId);
    const [sqrtPriceX96] = await client.readContract({
      address: pool,
      abi: PoolContractABI2,
      functionName: "slot0"
    });
    return sqrtPriceX96;
  }
  async getLiquidity(chainId, pool) {
    const result = this.liquidityCache.get(chainId, pool);
    if (result) {
      return result;
    }
    const client = getClient2(chainId);
    const liquidity = await client.readContract({
      address: pool,
      functionName: "liquidity",
      abi: PoolContractABI2
    });
    this.liquidityCache.set(chainId, pool, liquidity);
    return liquidity;
  }
  calculatePrice(sqrtPriceX96, sourceToken, destinationToken, liquidity, isRevertRate) {
    const Q96 = 2n ** 96n;
    const sourceTokenAmount = liquidity * Q96 / sqrtPriceX96;
    const destinationTokenAmount = liquidity * sqrtPriceX96 / Q96;
    return [
      BigMath2.div(
        isRevertRate ? sourceTokenAmount : destinationTokenAmount,
        isRevertRate ? destinationTokenAmount : sourceTokenAmount,
        destinationToken.decimals,
        sourceToken.decimals,
        destinationToken.decimals
      ),
      BigMath2.div(
        isRevertRate ? destinationTokenAmount : sourceTokenAmount,
        isRevertRate ? sourceTokenAmount : destinationTokenAmount,
        sourceToken.decimals,
        destinationToken.decimals,
        sourceToken.decimals
      )
    ];
  }
};

// libs/sdk/src/lib/tokens/token-rate/adapters/uniswap-v3-adapter.ts
function factoryContractGetter(chainId) {
  if (chainId === ChainId4.bnb) return "0xdB1d10011AD0Ff90774D0C6Bb92e5C5c8b4461F7";
  if (chainId === ChainId4.avalanche) return "0x740b1c1de25031C31FF4fC9A62f554A55cdC1baD";
  return "0x1F98431c8aD98523631AE4a59f267346ea31F984";
}
var uniswapV3Adapter = new UniswapV3BaseRateAdapter(
  "UniswapV3",
  factoryContractGetter,
  [
    ChainId4.eth,
    ChainId4.bnb,
    ChainId4.matic,
    ChainId4.op,
    ChainId4.arbitrum,
    ChainId4.avalanche
  ],
  [100, 500, 3e3, 1e4]
);

// libs/sdk/src/lib/tokens/token-rate/adapters/pancakeswap-v3-adapter.ts
import { ChainId as ChainId5 } from "@one-inch-community/models";
function factoryContractGetter2(chainId) {
  if (chainId === ChainId5.zkSyncEra) return "0x1BB72E0CbbEA93c08f535fc7856E0338D7F7a8aB";
  return "0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865";
}
var pancakeswapV3Adapter = new UniswapV3BaseRateAdapter(
  "PancakeSwapV3",
  factoryContractGetter2,
  [
    ChainId5.eth,
    ChainId5.bnb,
    ChainId5.arbitrum,
    ChainId5.zkSyncEra
  ],
  [100, 500, 2500, 1e4]
);

// libs/sdk/src/lib/tokens/token-rate/adapters/sushiswap-v3-adapter.ts
import { ChainId as ChainId6 } from "@one-inch-community/models";
var SushiSwapFactory2 = {
  [ChainId6.eth]: "0xbACEB8eC6b9355Dfc0269C18bac9d6E2Bdc29C4F",
  [ChainId6.bnb]: "0x126555dd55a39328F69400d6aE4F782Bd4C34ABb",
  [ChainId6.matic]: "0x917933899c6a5F8E37F31E19f92CdBFF7e8FF0e2",
  [ChainId6.gnosis]: "0xf78031CBCA409F2FB6876BDFDBc1b2df24cF9bEf",
  [ChainId6.fantom]: "0x7770978eED668a3ba661d51a773d3a992Fc9DDCB",
  [ChainId6.avalanche]: "0x3e603C14aF37EBdaD31709C4f848Fc6aD5BEc715",
  [ChainId6.arbitrum]: "0x1af415a1EbA07a4986a52B6f2e7dE7003D82231e",
  [ChainId6.op]: "0x9c6522117e2ed1fE5bdb72bb0eD5E3f2bdE7DBe0"
};
var sushiswapV3Adapter = new UniswapV3BaseRateAdapter(
  "SushiSwapV3",
  (chainId) => SushiSwapFactory2[chainId],
  [
    ChainId6.eth,
    ChainId6.bnb,
    ChainId6.op,
    ChainId6.matic,
    ChainId6.gnosis,
    ChainId6.fantom,
    ChainId6.arbitrum,
    ChainId6.avalanche
  ],
  [100, 500, 3e3, 1e4]
);

// libs/sdk/src/lib/tokens/token-rate/adapters/spookyswap-v3-adapter.ts
import { ChainId as ChainId7 } from "@one-inch-community/models";
var spookyswapV3Adapter = new UniswapV3BaseRateAdapter(
  "SpookySwapV3",
  () => "0x7928a2c48754501f3a8064765ECaE541daE5c3E6",
  [
    ChainId7.fantom
  ],
  [100, 500, 3e3, 1e4]
);

// libs/sdk/src/lib/tokens/token-rate/token-rate.provider.ts
import { BlockTimeCache as BlockTimeCache2 } from "@one-inch-community/sdk/chain";
import { CacheActivePromise } from "@one-inch-community/core/decorators";
var TokenRateProvider = class {
  constructor(onChainAdapters) {
    this.onChainAdapters = onChainAdapters;
    this.rateCache = new BlockTimeCache2();
  }
  async getOnChainRate(chainId, sourceToken, destinationToken) {
    const rate = await this.getOnChainRawRate(chainId, sourceToken, destinationToken);
    if (rate.length === 0) return null;
    const getRateVale = (rate2) => rate2.isReverted ? rate2.revertedRate : rate2.rate;
    let maxRate = rate[0];
    for (const item of rate) {
      if (getRateVale(item) > getRateVale(maxRate)) {
        maxRate = item;
      }
    }
    return maxRate;
  }
  listenOnChainRate(chainId, sourceToken, destinationToken) {
    return getBlockEmitter(chainId).pipe(
      startWith(null),
      switchMap(() => this.getOnChainRate(chainId, sourceToken, destinationToken))
    );
  }
  async getOnChainRawRate(chainId, sourceToken, destinationToken) {
    let _srcToken = sourceToken;
    let _dstToken = destinationToken;
    if (isNativeToken(sourceToken.address)) {
      const wToken = getWrapperNativeToken(chainId);
      if (!wToken) return [];
      _srcToken = wToken;
    }
    if (isNativeToken(destinationToken.address)) {
      const wToken = getWrapperNativeToken(chainId);
      if (!wToken) return [];
      _dstToken = wToken;
    }
    const id = [_srcToken.address, _dstToken.address].join(":");
    const cacheRateList = this.rateCache.get(chainId, id);
    if (cacheRateList !== null) {
      return cacheRateList;
    }
    const sources = this.onChainAdapters.filter((source) => source.isSupportedChain(chainId));
    const rateResultList = await Promise.all(sources.map((source) => source.getRate(chainId, _srcToken, _dstToken)));
    const cleanRateList = rateResultList.filter((rate) => rate !== null);
    this.rateCache.set(chainId, id, cleanRateList);
    return cleanRateList;
  }
};
__decorateClass([
  CacheActivePromise((_, chainId, sourceToken, destinationToken) => [chainId, sourceToken.address, destinationToken.address].join(":"))
], TokenRateProvider.prototype, "getOnChainRate", 1);
function buildDefaultTokenRateProvider() {
  return new TokenRateProvider([
    // oneInchOracleAdapter,
    // v3
    uniswapV3Adapter,
    pancakeswapV3Adapter,
    sushiswapV3Adapter,
    spookyswapV3Adapter,
    // v2
    uniswapV2Adapter,
    sushiswapV2Adapter,
    pancakeswapV2Adapter
  ]);
}

// libs/sdk/src/lib/tokens/token-usd-on-chain-price.provider.ts
import { ChainId as ChainId8 } from "@one-inch-community/models";
import { formatUnits, isAddressEqual as isAddressEqual3 } from "viem";
var USDTAddresses = {
  [ChainId8.eth]: "0xdac17f958d2ee523a2206206994597c13d831ec7",
  [ChainId8.bnb]: "0x55d398326f99059ff775485246999027b3197955",
  [ChainId8.matic]: "0xc2132d05d31c914a87c6611c10748aeb04b58e8f",
  [ChainId8.op]: "0x94b008aa00579c1307b0ef2c499ad98a8ce58e58",
  [ChainId8.arbitrum]: "0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9",
  [ChainId8.gnosis]: "0x4ecaba5870353805a9f068101a40e0f32ed605c6",
  [ChainId8.avalanche]: "0xc7198437980c041c805a1edcba50c1ce5db95118",
  [ChainId8.fantom]: "0xcc1b99ddac1a33c201a742a1851662e87bc7f22c",
  [ChainId8.aurora]: "0x4988a896b1227218e4a686fde5eabdcabd91571f",
  [ChainId8.klaytn]: "0x754288077d0ff82af7a5317c7cb8c444d421d103",
  // USDC
  [ChainId8.zkSyncEra]: "0x493257fd37edb34451f62edf8d2a0c418852ba4c"
};
var decimals = 6;
var TokenUsdOnChainPriceProvider = class {
  constructor() {
    this.rateProvider = buildDefaultTokenRateProvider();
  }
  async getPrice(chainId, token) {
    const USDTAddress = USDTAddresses[chainId];
    if (isAddressEqual3(token.address, USDTAddress)) {
      return "1";
    }
    const rate = await this.rateProvider.getOnChainRate(
      chainId,
      { address: USDTAddress, chainId, decimals, name: "USDT", symbol: "USDT" },
      token
    );
    if (!rate) return "0";
    return formatUnits(rate.rate, token.decimals);
  }
};

// libs/sdk/src/lib/tokens/token.schema.ts
import { isAddressEqual as isAddressEqual4 } from "viem";
import { nativeTokenAddress } from "@one-inch-community/sdk/chain";
import { QueueCache } from "@one-inch-community/core/cache";
var TokenPriority = {
  "native": 1e3,
  "USDT": 100,
  "USDC": 100,
  "WETH": 62,
  "1INCH": 61,
  "PEG:ETH": 60,
  "PEG:USD": 50,
  "PEG:BTC": 40,
  "PEG:EUR": 30,
  "staking": 20,
  "savings": 10,
  "tokens": 0
};
var TokenSchema = class _TokenSchema {
  constructor() {
    this.tokenCache = new QueueCache(50);
  }
  static {
    this.databaseVersion = 4;
  }
  get tokens() {
    if (!this.database) throw new Error("token database not init");
    return this.database.tokens;
  }
  get balances() {
    if (!this.database) throw new Error("token database not init");
    return this.database.balances;
  }
  async init() {
    const Dexie = await import("dexie").then((m) => m.Dexie);
    const db = new Dexie("one-inch-token");
    db.version(_TokenSchema.databaseVersion).stores({
      tokens: [
        "id",
        // 'chainId:address'
        "address",
        "decimals",
        "chainId",
        "name",
        "symbol",
        "*tags",
        "eip2612",
        "logoURL",
        "isFavorite",
        "priority"
      ].join(", "),
      balances: [
        "id",
        // 'chainId:walletAddress:tokenAddress'
        "chainId",
        "tokenAddress",
        "walletAddress",
        "amount"
      ].join(", ")
    });
    this.database = db;
  }
  async getToken(chainId, address) {
    const recordId = getId(chainId, address);
    if (this.tokenCache.has(recordId)) {
      return this.tokenCache.get(recordId);
    }
    const records = await this.tokens.where("id").equals(recordId).toArray();
    this.tokenCache.set(recordId, records[0]);
    return records[0] ?? null;
  }
  async getNativeToken(chainId) {
    return this.getToken(chainId, nativeTokenAddress);
  }
  getTokenBySymbol(chainId, symbol) {
    return this.tokens.where("chainId").equals(chainId).filter((record) => record.symbol === symbol).toArray();
  }
  async getTokenMap(chainId, addresses) {
    const addressesSet = new Set(addresses);
    const result = {};
    await this.tokens.each((record) => {
      if (record.chainId === chainId && addressesSet.has(record.address)) {
        result[record.address] = record;
      }
    });
    return result;
  }
  getTokenList(chainId, addresses) {
    const addressesSet = new Set(addresses);
    return this.tokens.filter((record) => record.chainId === chainId && addressesSet.has(record.address)).toArray();
  }
  async getTokenBalanceMap(chainId, walletAddress, addresses) {
    const addressesSet = new Set(addresses);
    const result = {};
    await this.balances.each((record) => {
      if (record.chainId === chainId && record.walletAddress === walletAddress && addressesSet.has(record.tokenAddress)) {
        result[record.tokenAddress] = BigInt(record.amount);
      }
    });
    return result;
  }
  async getTokens(chainId, addresses) {
    const addressesSet = new Set(addresses);
    return this.tokens.filter((record) => record.chainId === chainId && addressesSet.has(record.address)).toArray();
  }
  async getAllTokenAddresses(chainId) {
    const result = [];
    await this.tokens.where("chainId").equals(chainId).each((record) => result.push(record.address));
    return result;
  }
  async getTokenBalance(chainId, tokenAddress, walletAddress) {
    const recordId = getId(chainId, walletAddress, tokenAddress);
    const records = await this.balances.where("id").equals(recordId).toArray();
    return records[0] ?? null;
  }
  async isEmptyTokenBalanceStorage(chainId, walletAddress) {
    return this.balances.filter((record) => record.chainId === chainId && isAddressEqual4(record.walletAddress, walletAddress)).toArray().then((list) => list.length === 0);
  }
  async setTokens(chainId, tokens) {
    const tableTokens = [];
    for (const token of tokens) {
      tableTokens.push({
        id: getId(chainId, token.address),
        address: token.address,
        decimals: token.decimals,
        eip2612: token.extensions?.eip2612 ?? null,
        name: token.name,
        symbol: token.symbol,
        tags: token.tags,
        logoURL: token.logoURI,
        isFavorite: false,
        chainId,
        priority: calcTokenPriority(token)
      });
    }
    this.tokenCache.clear();
    await this.tokens.bulkPut(tableTokens);
  }
  async setBalances(chainId, walletAddress, balances) {
    const balancesRecords = [];
    for (const address in balances) {
      const tokenAddress = address;
      balancesRecords.push({
        id: getId(chainId, walletAddress, tokenAddress),
        chainId,
        tokenAddress,
        walletAddress,
        amount: balances[tokenAddress]
      });
    }
    await this.balances.bulkPut(balancesRecords);
  }
  async getAllFavoriteTokenAddresses(chainId) {
    const result = [];
    await this.tokens.where("chainId").equals(chainId).each((record) => {
      if (!record.isFavorite) return;
      result.push(record.address);
    });
    return result;
  }
  async getSortedByPriorityAndBalanceTokenAddresses(chainId, filterPattern, walletAddress) {
    const balances = {};
    const priority = {};
    const favoriteTokenSet = /* @__PURE__ */ new Set();
    const notZero = [];
    const zero = [];
    const _filterPattern = filterPattern.toLowerCase();
    if (walletAddress) {
      await this.balances.where("chainId").equals(chainId).and((record) => isAddressEqual4(record.walletAddress, walletAddress)).each((record) => balances[record.tokenAddress] = BigInt(record.amount));
    }
    await this.tokens.where("chainId").equals(chainId).each((record) => {
      if (_filterPattern !== "") {
        if (!record.address.toLowerCase().includes(_filterPattern) && !record.name.toLowerCase().startsWith(_filterPattern) && !record.symbol.toLowerCase().startsWith(_filterPattern)) return;
      }
      if (balances[record.address] && balances[record.address] > 0n) {
        notZero.push(record.address);
        return;
      }
      priority[record.address] = record.priority;
      zero.push(record.address);
      record.isFavorite && favoriteTokenSet.add(record.address);
    });
    return {
      notZero,
      zero: zero.sort((address1, address2) => {
        const priority1 = priority[address1];
        const priority2 = priority[address2];
        const isFavoriteToken1 = favoriteTokenSet.has(address1);
        const isFavoriteToken2 = favoriteTokenSet.has(address2);
        if (isFavoriteToken1 === isFavoriteToken2) {
          if (priority1 < priority2) {
            return 1;
          }
          if (priority1 > priority2) {
            return -1;
          }
          return 0;
        }
        if (isFavoriteToken1) {
          return -1;
        }
        if (isFavoriteToken2) {
          return 1;
        }
        return 0;
      })
    };
  }
  async setFavoriteState(chainId, tokenAddress, state) {
    const recordId = getId(chainId, tokenAddress);
    this.tokenCache.delete(recordId);
    await this.tokens.update(recordId, { isFavorite: state });
  }
  async setEip2612Support(chainId, address, state) {
    const recordId = getId(chainId, address);
    await this.tokens.update(recordId, { eip2612: state });
  }
};
function getId(...args) {
  return args.join(":");
}
function calcTokenPriority(dto) {
  let priority = dto.providers?.length ?? 0;
  priority += TokenPriority[dto.symbol] ?? 0;
  for (const tag of dto.tags) {
    priority += TokenPriority[tag] ?? 0;
  }
  return priority;
}

// libs/sdk/src/lib/tokens/token.controller.ts
import { formatUnits as formatUnits2 } from "viem";

// libs/sdk/src/lib/chain/average-block-time.ts
import { ChainId as ChainId9 } from "@one-inch-community/models";
var averageBlockTime = {
  [ChainId9.eth]: 11e3,
  [ChainId9.bnb]: 3e3,
  [ChainId9.matic]: 3e3,
  [ChainId9.op]: 3e3,
  [ChainId9.arbitrum]: 3e3,
  [ChainId9.gnosis]: 5e3,
  [ChainId9.avalanche]: 3e3,
  [ChainId9.fantom]: 3e3,
  [ChainId9.aurora]: 3e3,
  [ChainId9.klaytn]: 3e3,
  [ChainId9.zkSyncEra]: 3e3
};

// libs/sdk/src/lib/tokens/token.controller.ts
import { liveQuery } from "dexie";
import { storage, JsonParser } from "@one-inch-community/core/storage";
import { CacheActivePromise as CacheActivePromise2 } from "@one-inch-community/core/decorators";
import { isSupportedEIP2612 } from "@one-inch-community/sdk/chain";
import { getBalances } from "@one-inch-community/sdk/chain";
import { defer } from "rxjs";
var lastUpdateTokenDatabaseTimestampStorageKey = `last-update-token-database-timestamp-v${TokenSchema.databaseVersion}`;
var lastUpdateTokenBalanceDatabaseTimestampStorageKey = `last-update-token-balance-database-timestamp-v${TokenSchema.databaseVersion}`;
var tokenDatabaseTTL = 6048e5;
var tokenBalanceDatabaseTTL = averageBlockTime;
var TokenController = class {
  constructor() {
    this.schema = new TokenSchema();
    this.tokenUsdPriceProvider = new TokenUsdOnChainPriceProvider();
    this.lastUpdateTokenDatabaseTimestampStorage = storage.get(lastUpdateTokenDatabaseTimestampStorageKey, JsonParser);
    this.lastUpdateTokenBalanceDatabaseTimestampStorage = storage.get(lastUpdateTokenBalanceDatabaseTimestampStorageKey, JsonParser);
  }
  async init(context) {
    this.context = context;
    this.oneInchApiAdapter = context.oneInchDevPortalAdapter;
    await this.schema.init();
  }
  /**
   * Retrieves sorted by balances and priority token addresses.
   *
   * @param {ChainId} chainId - The ID of the chain.
   * @param {String} [filterPattern] - pattern for find token buy name or address
   * @param {Address} [walletAddress] - The connected wallet address. (Optional)
   * @returns {Promise<Address[]>} - A promise that resolves to an array of sorted token addresses.
   */
  async getSortedByPriorityAndBalanceTokenAddresses(chainId, filterPattern, walletAddress) {
    await this.updateTokenDatabase(chainId);
    if (walletAddress) {
      await this.updateBalanceDatabase(chainId, walletAddress);
    }
    const result = await this.schema.getSortedByPriorityAndBalanceTokenAddresses(chainId, filterPattern, walletAddress);
    if (walletAddress) {
      const prices = await this.getTokenUSDPrices(chainId, result.notZero);
      const tokens = await this.getTokenMap(chainId, result.notZero);
      const balances = await this.getTokenBalanceMap(chainId, walletAddress, result.notZero);
      const favoriteTokenList = await this.getAllFavoriteTokenAddresses(chainId);
      const favoriteTokenSet = new Set(favoriteTokenList);
      const tokenAmount = {};
      for (const address of result.notZero) {
        const token = tokens[address];
        const tokenPrice = prices[address];
        const balance = formatUnits2(balances[address], token.decimals);
        tokenAmount[address] = Number(balance) * Number(tokenPrice);
      }
      return [
        ...result.notZero.sort((address1, address2) => {
          const isFavoriteToken1 = favoriteTokenSet.has(address1);
          const isFavoriteToken2 = favoriteTokenSet.has(address2);
          if (isFavoriteToken1 === isFavoriteToken2) {
            return tokenAmount[address2] - tokenAmount[address1];
          }
          if (isFavoriteToken1) {
            return -1;
          }
          return 1;
        }),
        ...result.zero
      ];
    }
    return [
      ...result.notZero,
      ...result.zero
    ];
  }
  async getToken(chainId, address) {
    await this.updateTokenDatabase(chainId);
    return await this.schema.getToken(chainId, address);
  }
  async getNativeToken(chainId) {
    await this.updateTokenDatabase(chainId);
    return await this.schema.getNativeToken(chainId);
  }
  async getTokenBySymbol(chainId, symbol) {
    await this.updateTokenDatabase(chainId);
    return this.schema.getTokenBySymbol(chainId, symbol);
  }
  async getTokenList(chainId, addresses) {
    await this.updateTokenDatabase(chainId);
    return await this.schema.getTokenList(chainId, addresses);
  }
  async getTokenListSortedByPriority(chainId, addresses) {
    const tokens = await this.getTokenList(chainId, addresses);
    return tokens.sort((token1, token2) => token2.priority - token1.priority);
  }
  async getTokenMap(chainId, addresses) {
    return await this.schema.getTokenMap(chainId, addresses);
  }
  async getTokenBalanceMap(chainId, walletAddress, addresses) {
    await this.updateBalanceDatabase(chainId, walletAddress);
    return await this.schema.getTokenBalanceMap(chainId, walletAddress, addresses);
  }
  async getTokenBalance(chainId, tokenAddress, walletAddress) {
    await this.updateBalanceDatabase(chainId, walletAddress, tokenAddress);
    return await this.schema.getTokenBalance(chainId, tokenAddress, walletAddress);
  }
  async getTokenUSDPrice(chainId, tokenAddress) {
    const result = await this.getTokenUSDPrices(chainId, [tokenAddress]);
    return result[tokenAddress];
  }
  async isFavoriteToken(chainId, tokenAddress) {
    const token = await this.getToken(chainId, tokenAddress);
    return token?.isFavorite ?? false;
  }
  async getTokenLogoURL(chainId, tokenAddress) {
    const token = await this.getToken(chainId, tokenAddress);
    return token?.logoURL ?? null;
  }
  async getPriorityToken(chainId, addresses) {
    const tokens = await this.getTokenList(chainId, addresses);
    const isStable = (token) => token.tags.includes("PEG:USD") || token.tags.includes("PEG:EUR");
    return tokens.sort((record1, record2) => {
      const isStable1 = isStable(record1);
      const isStable2 = isStable(record2);
      if (isStable1 && isStable2) {
        return record1.priority - record2.priority;
      }
      if (isStable1 && !isStable2) {
        return -1;
      }
      if (!isStable1 && isStable2) {
        return 1;
      }
      return record1.priority - record2.priority;
    })[0];
  }
  async setFavoriteState(chainId, tokenAddress, state) {
    await this.schema.setFavoriteState(chainId, tokenAddress, state);
  }
  async getAllFavoriteTokenAddresses(chainId) {
    return await this.schema.getAllFavoriteTokenAddresses(chainId);
  }
  async getTokenUSDPrices(chainId, tokenAddressList) {
    const result = {};
    const prices = await this.oneInchApiAdapter.getTokenPrices(chainId).catch(() => null);
    if (prices === null) {
      const tokens = await this.schema.getTokens(chainId, tokenAddressList);
      await Promise.all(
        tokens.map((token) => this.tokenUsdPriceProvider.getPrice(chainId, token).catch(() => "0").then((price) => result[token.address] = price))
      );
      return result;
    }
    for (const address of tokenAddressList) {
      result[address] = prices[address] ?? "0";
    }
    return result;
  }
  async isSupportedTokenPermit(chainId, tokenAddress) {
    const token = await this.schema.getToken(chainId, tokenAddress);
    if (!token) return false;
    if (token.eip2612 === null) {
      const state = await isSupportedEIP2612(chainId, tokenAddress);
      await this.schema.setEip2612Support(chainId, tokenAddress, state);
      return state;
    }
    return token.eip2612;
  }
  async updateTokenDatabase(chainId) {
    const lastUpdateTime = this.lastUpdateTokenDatabaseTimestampStorage?.[chainId];
    if (lastUpdateTime && Date.now() - lastUpdateTime < tokenDatabaseTTL) return;
    const tokens = await this.oneInchApiAdapter.getWhiteListedTokens(chainId);
    await this.schema.setTokens(chainId, tokens);
    storage.updateEntity(lastUpdateTokenDatabaseTimestampStorageKey, chainId.toString(), Date.now());
    this.lastUpdateTokenDatabaseTimestampStorage = storage.get(lastUpdateTokenDatabaseTimestampStorageKey, JsonParser);
  }
  async updateBalanceDatabase(chainId, walletAddress, tokenAddress) {
    const id = [chainId, walletAddress].join(":");
    const lastUpdateTime = this.lastUpdateTokenBalanceDatabaseTimestampStorage?.[id];
    if (lastUpdateTime && Date.now() - lastUpdateTime < tokenBalanceDatabaseTTL[chainId]) return;
    const update = async () => {
      let balances = await this.oneInchApiAdapter.getBalancesByWalletAddress(chainId, walletAddress).catch(() => null);
      if (balances === null) {
        balances = await this.getBalancesOnChain(chainId, walletAddress);
      }
      await this.schema.setBalances(chainId, walletAddress, balances);
      storage.updateEntity(lastUpdateTokenBalanceDatabaseTimestampStorageKey, id, Date.now());
      this.lastUpdateTokenBalanceDatabaseTimestampStorage = storage.get(lastUpdateTokenBalanceDatabaseTimestampStorageKey, JsonParser);
    };
    if (tokenAddress) {
      const tokenBalances = await this.schema.getTokenBalance(chainId, tokenAddress, walletAddress);
      if (tokenBalances === null) return await update();
    } else if (await this.schema.isEmptyTokenBalanceStorage(chainId, walletAddress)) {
      return await update();
    }
    update().catch((error) => console.error(error));
  }
  liveQuery(querier) {
    return defer(() => liveQuery(querier));
  }
  async getBalancesOnChain(chainId, walletAddress) {
    const tokens = await this.schema.getAllTokenAddresses(chainId);
    return getBalances(chainId, walletAddress, tokens);
  }
};
__decorateClass([
  CacheActivePromise2()
], TokenController.prototype, "updateTokenDatabase", 1);
__decorateClass([
  CacheActivePromise2()
], TokenController.prototype, "updateBalanceDatabase", 1);

// libs/sdk/src/lib/tokens/is-tokens-equal.ts
import { isAddressEqual as isAddressEqual5 } from "viem";
function isTokensEqual(token1, token2) {
  const isAddressMatching = isAddressEqual5(token1.address, token2.address);
  const isChainIdMatching = token1.chainId === token2.chainId;
  return isAddressMatching && isChainIdMatching;
}

// libs/sdk/src/lib/tokens/is-rate-equal.ts
function isRateEqual(rate1, rate2) {
  return rate1.rate === rate2.rate && rate1.chainId === rate2.chainId && rate1.revertedRate === rate2.revertedRate && isTokensEqual(rate1.sourceToken, rate2.sourceToken) && isTokensEqual(rate1.destinationToken, rate2.destinationToken);
}
export {
  TokenController,
  TokenRateProvider,
  TokenUsdOnChainPriceProvider,
  buildDefaultTokenRateProvider,
  isRateEqual,
  isTokensEqual
};
//# sourceMappingURL=index.esm.js.map
